// Record mode: capture real backend responses so they can be saved as the
// replay fixtures bundle.
//
// Run `npm run dev:record` (Vite proxies /api to a real local backend), browse
// every page you want in the demo, then click the floating "Download fixtures"
// button. Drop the resulting demo-fixtures.json into public/ and build with
// `npm run build:demo`.

import { signature } from './signature';
import type { Fixture } from './fixtures';

const captured = new Map<string, Fixture>();

export async function captureResponse(method: string, url: string, res: Response): Promise<void> {
  // Only GET JSON responses are useful as replay fixtures; writes and binary
  // media (audio) are synthesized/skipped on replay.
  if (method.toUpperCase() !== 'GET') return;
  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) return;
  if (!res.ok) return;

  try {
    const body = await res.json();
    captured.set(signature('GET', url), { status: res.status, body });
    updateBadge();
  } catch {
    /* non-JSON or empty body — skip */
  }
}

function buildBundle(): string {
  return JSON.stringify(Object.fromEntries(captured), null, 2);
}

function download(): void {
  const blob = new Blob([buildBundle()], { type: 'application/json' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'demo-fixtures.json';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
}

let badge: HTMLButtonElement | null = null;

function updateBadge(): void {
  if (badge) badge.textContent = `⬇ Download fixtures (${captured.size})`;
}

export function mountRecorderUi(): void {
  // Console escape hatch in addition to the button.
  (window as unknown as Record<string, unknown>).__demoDownloadFixtures = download;

  const attach = () => {
    if (badge || !document.body) return;
    badge = document.createElement('button');
    badge.type = 'button';
    badge.style.cssText = [
      'position:fixed',
      'bottom:16px',
      'right:16px',
      'z-index:99999',
      'padding:10px 14px',
      'background:#0d6efd',
      'color:#fff',
      'border:none',
      'border-radius:8px',
      'font:600 13px system-ui,sans-serif',
      'box-shadow:0 2px 8px rgba(0,0,0,.3)',
      'cursor:pointer',
    ].join(';');
    badge.onclick = download;
    updateBadge();
    document.body.appendChild(badge);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attach);
  } else {
    attach();
  }
  console.info('[demo] RECORD mode — browse the pages you want, then click "Download fixtures".');
}
